// ISDP CUSTOM: Resolve the signed-in user's DevHub role from their JWT group
// claims (the same groups the backend permission policy enforces), and publish it
// to the public-portal bridge so partners see unlocked product tabs.
//
// DEV-ONLY simulator: on localhost, a `?devrole=public|partner|draeger|admin`
// query param (or the persisted `devhub-dev-role` localStorage key) overrides the
// real role so each role's experience can be exercised without real Okta logins.
// This override is hard-gated to localhost and has no effect in production.
import { useEffect, useState } from 'react';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  roleFromGroups,
  entitledProducts,
  groupsForRole,
  canAccessInternalApp,
  isDevHubRole,
  featureEnabled,
  type DevHubRole,
} from '@internal/plugin-isdp-common';
import { writePortalSession, markSignedIn, clearSignedIn } from './portalSession';
import {
  readPreviewRole,
  previewToRole,
  previewProducts,
  previewFaceProducts,
  type PreviewRole,
} from './previewRole';

const DEV_ROLE_KEY = 'devhub-dev-role';

export function isLocalhost(): boolean {
  return (
    typeof window !== 'undefined' &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
  );
}

/** Read & persist a `?devrole=` override (localhost only). Returns the override. */
export function readDevRoleOverride(): DevHubRole | null {
  if (!isLocalhost()) return null;
  try {
    const param = new URLSearchParams(window.location.search).get('devrole');
    if (param === 'clear') {
      localStorage.removeItem(DEV_ROLE_KEY);
      return null;
    }
    if (param && isDevHubRole(param)) {
      localStorage.setItem(DEV_ROLE_KEY, param);
      return param;
    }
    const stored = localStorage.getItem(DEV_ROLE_KEY);
    return stored && isDevHubRole(stored) ? stored : null;
  } catch {
    return null;
  }
}

export interface UserRoleState {
  loading: boolean;
  /** Effective role: the previewed role when an internal user is previewing, else the real role. */
  role: DevHubRole;
  groups: string[];
  products: string[];
  /** True when a localhost dev override is in effect. */
  simulated: boolean;
  /** The user's real role, ignoring any active internal preview. */
  realRole: DevHubRole;
  /** True when the real user is a Dräger employee/admin (may enter the internal hub). */
  isInternal: boolean;
  /** The face an internal user is previewing (anonymous/public/partner), or null. */
  previewing: PreviewRole | null;
}

export function useUserRole(): UserRoleState {
  const identityApi = useApi(identityApiRef);
  const partnerPortal = featureEnabled(useApi(configApiRef), 'partnerPortal');
  const [state, setState] = useState<UserRoleState>({
    loading: true,
    role: 'public',
    groups: [],
    products: [],
    simulated: false,
    realRole: 'public',
    isInternal: false,
    previewing: null,
  });

  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (!active) return;
        let groups = identity.ownershipEntityRefs.filter(r =>
          r.startsWith('group:'),
        );
        let realRole = roleFromGroups(groups);
        let simulated = false;

        // Localhost-only dev simulator (?devrole=…) overrides the real role.
        const override = readDevRoleOverride();
        if (override) {
          realRole = override;
          groups = groupsForRole(override);
          simulated = true;
        }

        // A signed-in identity resolved — record presence so index.tsx routes future
        // public-path loads through the identity-capable app (not the anonymous shell).
        markSignedIn();

        const isInternal = canAccessInternalApp(realRole);

        // Internal "preview as" — honored ONLY for real internal users, so it can
        // never escalate an external user. Rewrites the effective role/groups/products.
        // "Preview as" only previews the external Partner Portal, so it means nothing
        // while that portal is out of the release — and a preview flag left in
        // localStorage by an earlier release must not strand an internal user outside
        // the hub. Skip it entirely when the portal is off.
        let role = realRole;
        let previewing: PreviewRole | null = null;
        if (isInternal && partnerPortal) {
          const preview = readPreviewRole();
          // The 'admin' face reveals admin-only/unreleased partner surfaces, so it is honored
          // ONLY for a real global admin. The external faces (anonymous/public/partner) are
          // honored for any internal user. A draeger (non-admin internal) previewing 'admin'
          // is ignored — never an escalation.
          if (preview && (preview !== 'admin' || realRole === 'admin')) {
            previewing = preview;
            role = previewToRole(preview);
            groups = groupsForRole(role);
          }
        }

        // The REAL user's entitlement: a simulated role or a real internal user is
        // entitled to every product (client-side wildcard); a real partner to their
        // premium groups. A previewed face has its own list (all, chosen, or none).
        const realProducts =
          simulated || canAccessInternalApp(realRole)
            ? previewProducts(realRole)
            : entitledProducts(groups);
        const products = previewing
          ? previewFaceProducts(previewing)
          : realProducts;
        // The bridge to the anonymous portal bundle records who is REALLY signed in, never
        // a previewed face — a preview is per-tab presentation, not an identity.
        writePortalSession(realRole, realProducts);
        setState({
          loading: false,
          role,
          groups,
          products,
          simulated,
          realRole,
          isInternal,
          previewing,
        });
      })
      .catch(() => {
        // No identity — treat as signed-out so index.tsx falls back to the anonymous shell.
        clearSignedIn();
        if (active) setState(s => ({ ...s, loading: false }));
      });
    return () => {
      active = false;
    };
    // partnerPortal is read inside the effect (it decides whether an internal user's
    // "preview as" is honoured), so it belongs in the deps; it is config, so it never changes
    // at runtime and this does not cause re-runs in practice.
  }, [identityApi, partnerPortal]);

  return state;
}
